import React, { useContext, useMemo, useState } from 'react';
import { CustomerServiceContext } from './CustomerServiceContext';
import { ICategorizedArticle } from './ICustomerService';
import Link from './Link';
import MinusIcon from './MinusIcon';
import PlusIcon from './PlusIcon';
import { useTranslation } from 'react-i18next';
import { en, fr } from './i18n';
import HelpChatIcon from './HelpChatIcon';

export interface ArticleSideNavigationProps {
    categorizedArticles: ICategorizedArticle[];
}

const AllContactUrls = ['contact-info', 'email-inquiry', 'billing-refund-inquiry', 'send-feedback'] as const;
type ContactUrlTuple = typeof AllContactUrls;
export type ContactUrl = ContactUrlTuple[number];

export function isContactUrl(value: string): value is ContactUrl {
    return AllContactUrls.includes(value as ContactUrl);
}

export interface Contact {
    urlName: ContactUrl;
    text: string;
}

const ArticleSideNavigation = ({ categorizedArticles }: ArticleSideNavigationProps) => {
    const { i18n, t } = useTranslation('customer-service');
    i18n.addResourceBundle('en', 'customer-service', en, true, true);
    i18n.addResourceBundle('fr', 'customer-service', fr, true, true);

    const { setView, setUrlName, setContact } = useContext(CustomerServiceContext);

    const [contactSection] = useState<Array<Contact>>(() => {
        return [
            {
                urlName: 'contact-info',
                text: t('contactInfo'),
            },
            {
                urlName: 'email-inquiry',
                text: t('emailInquiry'),
            },
            {
                urlName: 'billing-refund-inquiry',
                text: t('billingRefundInquiry'),
            },
            {
                urlName: 'send-feedback',
                text: t('sendFeedback'),
            },
        ];
    });

    const [expandedIndex, setExpandedIndex] = useState(() => {
        for (let i = 0; i < categorizedArticles.length; i++) {
            const categorizedArticle = categorizedArticles[i];
            for (let j = 0; j < categorizedArticle.Articles.length; j++) {
                if (window.location.href.includes(categorizedArticle.Articles[j].UrlName)) {
                    return i;
                }
            }
        }

        for (let i = 0; i < AllContactUrls.length; i++) {
            if (window.location.href.includes(AllContactUrls[i])) {
                return categorizedArticles.length;
            }
        }

        return -1;
    });

    return (
        <>
            <aside className="customer-service-faq__article__side-nav">
                <nav className="customer-service-faq__article__side-nav__nav">
                    <ul>
                        <li className="customer-service-faq__article__side-nav__nav__help">
                            <button
                                className="customer-service-faq__article__side-nav__nav__help__text"
                                onClick={() => {
                                    setView('main');
                                    setContact(null);
                                    window.history.pushState({}, '', '/pages/customer-service');
                                }}
                            >
                                {t('tscHelpCenter')}
                            </button>
                        </li>
                        {categorizedArticles.map((categorizedArticle, categorizedArticleIndex) => (
                            <li
                                className="customer-service-faq__article__side-nav__nav__item"
                                key={categorizedArticle.Category.Name}
                            >
                                <button
                                    className="customer-service-faq__article__side-nav__nav__item__title"
                                    onClick={() => {
                                        setExpandedIndex((prev) =>
                                            prev === categorizedArticleIndex ? -1 : categorizedArticleIndex,
                                        );
                                    }}
                                >
                                    <div className="customer-service-faq__article__side-nav__nav__item__title__lhs">
                                        <img
                                            className="customer-service-faq__article__side-nav__nav__item__title__lhs__icon"
                                            src={categorizedArticle.Category.ImageUrl}
                                            alt={`${categorizedArticle.Category.Name} icon`}
                                        />
                                        <span className="customer-service-faq__article__side-nav__nav__item__title__lhs__text">
                                            {categorizedArticle.Category.Name}
                                        </span>
                                    </div>
                                    <div className="customer-service-faq__article__side-nav__nav__item__title__rhs">
                                        {categorizedArticleIndex === expandedIndex ? <MinusIcon /> : <PlusIcon />}
                                    </div>
                                </button>
                                {categorizedArticleIndex === expandedIndex && (
                                    <ul className="customer-service-faq__article__side-nav__nav__item__articles">
                                        {categorizedArticle.Articles.map((article) => (
                                            <li
                                                className="customer-service-faq__article__side-nav__nav__item__articles__link"
                                                key={article.UrlName}
                                            >
                                                <Link
                                                    href={`/pages/customer-service/${article.UrlName}`}
                                                    dangerouslySetInnerHTML={{ __html: article.Title }}
                                                    className={`customer-service-faq__article__side-nav__nav__item__articles__link-text${
                                                        window.location.href.includes(article.UrlName)
                                                            ? '--current'
                                                            : ''
                                                    }`}
                                                    onClick={() => {
                                                        setView('article');
                                                        setUrlName(article.UrlName);
                                                        setContact(null);
                                                    }}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}

                        <li className="customer-service-faq__article__side-nav__nav__item" key="contact-tsc">
                            <button
                                className="customer-service-faq__article__side-nav__nav__item__title"
                                onClick={() => {
                                    setExpandedIndex((prev) =>
                                        prev === categorizedArticles.length ? -1 : categorizedArticles.length,
                                    );
                                }}
                            >
                                <div className="customer-service-faq__article__side-nav__nav__item__title__lhs">
                                    <HelpChatIcon
                                        className="customer-service-faq__article__side-nav__nav__item__title__lhs__icon"
                                        width={25}
                                        height={25}
                                    />
                                    <span className="customer-service-faq__article__side-nav__nav__item__title__lhs__text">
                                        {t('contactTsc')}
                                    </span>
                                </div>
                                <div className="customer-service-faq__article__side-nav__nav__item__title__rhs">
                                    {categorizedArticles.length === expandedIndex ? <MinusIcon /> : <PlusIcon />}
                                </div>
                            </button>
                            {categorizedArticles.length === expandedIndex && (
                                <ul className="customer-service-faq__article__side-nav__nav__item__articles">
                                    {contactSection.map((article) => (
                                        <li
                                            className="customer-service-faq__article__side-nav__nav__item__articles__link"
                                            key={article.urlName}
                                        >
                                            <Link
                                                href={`/pages/customer-service/${article.urlName}`}
                                                dangerouslySetInnerHTML={{ __html: article.text }}
                                                className={`customer-service-faq__article__side-nav__nav__item__articles__link-text${
                                                    window.location.href.includes(article.urlName) ? '--current' : ''
                                                }`}
                                                onClick={() => {
                                                    setView('article');
                                                    setUrlName(article.urlName);
                                                }}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    </ul>
                </nav>
            </aside>
        </>
    );
};

export default ArticleSideNavigation;
